import React from 'react';

// --- DATA IMPORTS (All 18 Banks) ---
import { blufBank } from '../../data/BLUFBank';
import { cultureVibeBank } from '../../data/CultureVibeBank';
import { eqBank } from '../../data/EmotionalIntelligenceBank';
import { expenseRouletteBank } from '../../data/ExpenseRouletteBank';
import { chronosBank } from '../../data/FocusTimeBank';
import { gatekeepingBank } from '../../data/GatekeepingBank';
import { gauntletBank } from '../../data/GauntletBank';
import { interviewMasteryBank } from '../../data/InterviewMasteryBank';
import { leadershipMicroBank } from '../../data/LeadershipMicroBank';
import { mediumMattersBank } from '../../data/MediumMattersBank';
import { meetingHeroBank } from '../../data/MeetingHeroBank';
import { modernNetworkingBank } from '../../data/ModernNetworkingBank';
import { officeEtiquetteBank } from '../../data/OfficeEtiquetteBank';
import { oneTouchBank } from '../../data/OneTouchBank';
import { salaryNegotiationBank } from '../../data/SalaryNegotiationBank';
import { scenarioBank } from '../../data/ScenarioBank';
import { techStackBank } from '../../data/TechStackBank';
import { visualRulesBank } from '../../data/VisualRulesBank';

export default function CurriculumAppendix() {
  const handlePrint = () => window.print();

  // --- HELPER RENDERERS FOR VARYING JSON SCHEMAS ---

  // 1. Standard Multiple Choice & Situational Judgment
  const renderSJT = (title, bank, promptKey, optionsKey, correctKey = 'correct', textKey = 'text') => (
    <section className="mb-10 break-inside-avoid">
      <h2 className="text-xl font-bold border-b-2 border-slate-800 mb-4 pb-1 uppercase tracking-widest">{title}</h2>
      <div className="space-y-6">
        {bank.map((q, idx) => (
          <div key={q.id || idx} className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="font-bold mb-3 text-sm">{idx + 1}. {q[promptKey]}</p>
            
            {/* INJECT: Catch Parent-Level Hints or Context Variables */}
            {(q.hintText || q.failureFeedback || q.rationale) && (
                <p className="text-xs text-slate-600 italic mb-3 bg-white p-2 border rounded">
                   <strong>Instructor Note:</strong> {q.hintText || q.failureFeedback || q.rationale}
                </p>
            )}

            <ul className="space-y-2 ml-4 text-sm">
              {q[optionsKey]?.map((opt, i) => {
                const isOptimal = opt[correctKey] === true || opt.category === 'Optimal' || opt.type === 'Optimal';
                return (
                  <li key={i} className={`p-2 rounded border ${isOptimal ? "bg-emerald-50 border-emerald-200 text-emerald-900 font-bold" : "bg-white border-slate-100 text-slate-500"}`}>
                    {/* Handle complex objects like BLUF Bank */}
                    {opt.subject ? (
                      <div className="flex flex-col gap-1">
                        <span><strong>Subj:</strong> {opt.subject}</span>
                        <span><strong>Body:</strong> {opt.opener} {opt.ask}</span>
                      </div>
                    ) : (
                      <span>{opt[textKey] || opt.responseText || opt.actionText || opt.nodeTitle}</span>
                    )}
                    {isOptimal && <span className="ml-2 inline-block px-2 py-0.5 bg-emerald-200 text-emerald-800 text-[10px] uppercase rounded-full">Optimal Response</span>}
                    
                    {/* EXPANDED: Option-Level Feedback Fallback Chain */}
                    {(opt.feedback || opt.rationale || opt.hintText) && (
                        <p className="text-xs italic mt-1 text-slate-500">
                           Rationale: {opt.feedback || opt.rationale || opt.hintText}
                        </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );

  // 2. Binary / Swipe Mechanics
  const renderBinary = (title, bank, promptKey = 'text') => (
    <section className="mb-10 break-inside-avoid">
      <h2 className="text-xl font-bold border-b-2 border-slate-800 mb-4 pb-1 uppercase tracking-widest">{title}</h2>
      <table className="w-full text-left text-sm border-collapse border border-slate-300">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-300 p-2 w-1/2">Scenario</th>
            <th className="border border-slate-300 p-2 w-1/6">Validation</th>
            <th className="border border-slate-300 p-2 w-1/3">Policy Rationale</th>
          </tr>
        </thead>
        <tbody>
          {bank.map((item, idx) => {
            // Normalize correct answers across different swipe schemas
            let answer = item.correctId === 'A' ? 'Left/Reject' : item.correctId === 'B' ? 'Right/Accept' : item.correctAnswer || item.correctDirection || 'Validate';
            
            // Tech Stack specific override
            if (item.options) {
                const correctOpt = item.options.find(o => o.isCorrect);
                answer = correctOpt ? correctOpt.route : answer;
            }

            return (
              <tr key={item.id || idx} className="border-b border-slate-200">
                <td className="border border-slate-300 p-2 font-medium">{item[promptKey] || item.artifactDescription || item.scenarioText}</td>
                <td className="border border-slate-300 p-2 font-bold text-indigo-700">{answer}</td>
                
                {/* INJECT item.rationale HERE */}
                <td className="border border-slate-300 p-2 italic text-slate-600">
                  {item.feedback || item.feedbackRationale || item.rationale || item.options?.find(o=>o.isCorrect)?.feedback}
                </td>
                
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );

  // 3. Sequential / Chat Mechanics
  const renderSequence = (title, bank, promptKey) => (
    <section className="mb-10 break-inside-avoid">
      <h2 className="text-xl font-bold border-b-2 border-slate-800 mb-4 pb-1 uppercase tracking-widest">{title}</h2>
      <div className="grid grid-cols-1 gap-4">
        {bank.map((item, idx) => (
          <div key={item.id || idx} className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm">
            <p className="font-bold mb-2 text-slate-800">Trigger: "{item[promptKey]}"</p>
            <div className="bg-white border border-emerald-200 p-3 rounded text-emerald-900 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 block mb-1">Optimal Sequence Construction:</span>
              {(item.optimalSequence || item.optimalMessageSequence)?.join(" ")}
            </div>
            
            {/* EXPANDED: Sequence-Level Feedback Fallback Chain */}
            {(item.failureFeedback || item.feedback || item.rationale || item.hintText) && (
                <div className="bg-red-50 border border-red-100 p-3 rounded text-red-900 mt-2">
                   <span className="text-[10px] font-bold uppercase tracking-widest text-red-600 block mb-1">Failure Trap:</span>
                   <p className="text-xs italic">{item.failureFeedback || item.feedback || item.rationale || item.hintText}</p>
                </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-white p-8 font-serif text-black max-w-5xl mx-auto">
      
      {/* NO-PRINT CONTROLS: Hidden when the document is generated */}
      <div className="print:hidden mb-8 flex justify-between items-center border-b pb-4">
        <div>
            <h1 className="text-2xl font-black text-slate-800">Curriculum & Telemetry Appendix</h1>
            <p className="text-sm text-slate-500">18 Databanks Loaded</p>
        </div>
        <button 
          onClick={handlePrint}
          className="px-6 py-3 bg-slate-900 hover:bg-black text-white font-bold rounded-lg shadow-md transition-all active:scale-95"
        >
          Print / Save as PDF
        </button>
      </div>

      {/* PRINTABLE DOSSIER HEADER */}
      <div className="mb-12 text-center border-b-4 border-slate-900 pb-6">
        <h1 className="text-4xl font-black uppercase tracking-widest text-slate-900 mb-2">JobVantage Audit Matrix</h1>
        <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Confidential Answer Key & Rationale Ledger</p>
      </div>

      {/* --- CATEGORY 1: COMPLEX MULTIPLE CHOICE & SJT --- */}
      {renderSJT("Final Exam: The Gauntlet", gauntletBank, "text", "options")}
      {renderSJT("Leadership Micro: Executive Ownership", leadershipMicroBank, "scenarioContext", "options")}
      {renderSJT("Meeting Hero: Meeting Mechanics", meetingHeroBank, "leaderPrompt", "options")}
      {renderSJT("Interview Mastery: Adaptive Opponent", interviewMasteryBank, "adversaryPrompt", "options")}
      {renderSJT("Salary Negotiation: Leverage Tactics", salaryNegotiationBank, "recruiterPrompt", "options")}
      {renderSJT("Modern Networking: Spatial Directory", modernNetworkingBank, "scenarioContext", "options")}
      {renderSJT("Professionalism: The BLUF Method", blufBank, "scenarioContext", "options")}

      {/* --- CATEGORY 2: BINARY TRIAGE & COMPLIANCE SWIPES --- */}
      {renderBinary("Expense Roulette: T&E Fraud", expenseRouletteBank)}
      {renderBinary("Culture Vibe: Toxic vs Healthy", cultureVibeBank)}
      {renderBinary("Visual Rules: Camera Hygiene", visualRulesBank)}
      {renderBinary("Office Etiquette: CLM vs Pro Move", officeEtiquetteBank)}
      {renderBinary("Tech Stack: System of Record vs Shadow IT", techStackBank)}
      {renderBinary("Medium Matters: Channel Selection", mediumMattersBank)}
      {renderBinary("Gatekeeping: Urgency vs Importance", gatekeepingBank)}

      {/* --- CATEGORY 3: SEQUENCE & FRAGMENT ASSEMBLY --- */}
      {renderSequence("Emotional Intelligence: Fragment Synthesis", eqBank, "incomingMessage")}
      {renderSequence("One-Touch Rule: Message Optimization", oneTouchBank, "scenarioContext")}

      {/* --- CATEGORY 4: SPECIALIZED TRIAGE --- */}
      <section className="mb-10 break-inside-avoid">
        <h2 className="text-xl font-bold border-b-2 border-slate-800 mb-4 pb-1 uppercase tracking-widest">Focus Time: Chronos Triage</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {chronosBank.map((item) => (
            <div key={item.id} className="flex flex-col border border-slate-200 p-3 rounded-lg bg-slate-50">
              <span className="font-bold text-slate-500 uppercase text-[10px] mb-1">{item.distractionSource}</span>
              <p className="font-medium mb-2 text-slate-800">"{item.messageText}"</p>
              <div className="mt-auto pt-2 border-t border-slate-200">
                <span className="text-indigo-700 font-bold block mb-1">Optimal Action: {item.optimalTriage}</span>
                <span className="text-slate-600 italic text-xs leading-relaxed">{item.rationale}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* --- CATEGORY 5: DIAGNOSTIC BASELINE (Archetypes) --- */}
      <section className="mb-10 break-inside-avoid">
        <h2 className="text-xl font-bold border-b-2 border-slate-800 mb-4 pb-1 uppercase tracking-widest">Diagnostic: Archetype Engine Baseline</h2>
        <p className="text-sm text-slate-500 mb-4 italic">Note: These questions do not have "correct" answers. They map to the Holland Code (RIASEC) risk matrices.</p>
        <div className="grid grid-cols-1 gap-2 text-sm">
          {scenarioBank.filter(s => s.category !== 'Spatial / Kinematic Drills').map((item, idx) => (
            <div key={item.id || idx} className="p-3 bg-white border border-slate-200 rounded shadow-sm">
               <span className="font-bold text-[10px] text-slate-400 uppercase mr-2">{item.category}</span>
               <span className="font-medium">{item.promptText}</span>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}